import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import blogService from '../../services/blogService';

const BlogManagement = () => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    tags: [],
    status: 'draft',
    image: null,
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const data = await blogService.getAllBlogsAdmin();
      setBlogs(data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const handleOpen = (blog = null) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        content: blog.content,
        summary: blog.summary,
        tags: blog.tags,
        status: blog.status,
        image: null,
      });
    } else {
      setEditingBlog(null);
      setFormData({
        title: '',
        content: '',
        summary: '',
        tags: [],
        status: 'draft',
        image: null,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingBlog(null);
    setFormData({
      title: '',
      content: '',
      summary: '',
      tags: [],
      status: 'draft',
      image: null,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await blogService.updateBlog(editingBlog._id, formData);
      } else {
        await blogService.createBlog(formData);
      }
      handleClose();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('Are you sure you want to delete this blog post?'))) {
      try {
        await blogService.deleteBlog(id);
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h4' component='h1'>
          {t('Blog Management')}
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          {t('Create Blog')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Blog Title')}</TableCell>
              <TableCell>{t('Blog Status')}</TableCell>
              <TableCell>{t('Blog Tags')}</TableCell>
              <TableCell>{t('Created At')}</TableCell>
              <TableCell>{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blogs.map((blog) => (
              <TableRow key={blog._id}>
                <TableCell>{blog.title}</TableCell>
                <TableCell>
                  <Chip
                    label={blog.status}
                    color={blog.status === 'published' ? 'success' : 'default'}
                    size='small'
                  />
                </TableCell>
                <TableCell>
                  {blog.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size='small'
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  {new Date(blog.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(blog)} size='small'>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(blog._id)}
                    size='small'
                    color='error'
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>
          {editingBlog ? t('Edit Blog') : t('Create Blog')}
        </DialogTitle>
        <DialogContent>
          <Box component='form' onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('Blog Title')}
                  name='title'
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('Blog Summary')}
                  name='summary'
                  value={formData.summary}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('Blog Content')}
                  name='content'
                  value={formData.content}
                  onChange={handleInputChange}
                  multiline
                  rows={6}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  select
                  label={t('Blog Status')}
                  name='status'
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <MenuItem value='draft'>{t('Draft')}</MenuItem>
                  <MenuItem value='published'>{t('Published')}</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label={t('Add Tag')}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button onClick={handleAddTag} sx={{ ml: 1 }}>
                    {t('Add')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <input
                  accept='image/*'
                  type='file'
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id='blog-image'
                />
                <label htmlFor='blog-image'>
                  <Button variant='outlined' component='span'>
                    {t('Upload Image')}
                  </Button>
                </label>
                {formData.image && (
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {formData.image.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('Cancel')}</Button>
          <Button onClick={handleSubmit} variant='contained'>
            {editingBlog ? t('Update') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BlogManagement;
